import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { photoURL } from "@/data/plates";
import { IconBack } from "./icons";

/* Peças que toda tela usa. Nenhuma cor literal: tudo sai das fichas em
   ficha (`fichas.css`, gerado de `packages/core/src/fichas.ts`), senão
   um dos dois temas quebra em silêncio. */

/**
 * `onBack` existe além de `back` porque nem todo voltar é navegar. Na tela
 * de publicar ele volta para a câmera, que é um passo dentro da MESMA
 * rota — com um link, a seta jogaria a pessoa para fora do desafio e a
 * foto recém-tirada iria junto.
 */
export function TopBar(
  { title, back, onBack, action }:
  { title?: string; back?: string; onBack?: () => void; action?: ReactNode },
) {
  return (
    <div className="topbar">
      {onBack
        ? <button className="topbar-btn" onClick={onBack} aria-label="Voltar"><IconBack /></button>
        : back
          ? <Link className="topbar-btn" to={back} aria-label="Voltar"><IconBack /></Link>
          : <span className="topbar-btn" />}
      <div className="topbar-title">{title ?? ""}</div>
      {action ?? <span className="topbar-btn" />}
    </div>
  );
}

export function Spinner({ label = "Carregando" }: { label?: string }) {
  return <div className="spinner" role="status" aria-label={label} />;
}

/**
 * A medalha de iniciais — ou a foto, quando há.
 *
 * O que chega em `photo` pode ser duas coisas: um caminho do Storage
 * (quem trocou a foto à mão) ou uma URL pronta (quem entrou com o
 * Google). Jogar o valor direto no `<img>` deixava a foto escolhida à
 * mão invisível — caminho não é URL. Quem resolve é o `photoURL`, o
 * mesmo caminho das fotos de prato, porque dois jeitos de resolver foto
 * é como esta divergência começou (o app nativo passou pelo mesmo e o
 * conserto dele está em `nativo/src/ui/Foto.tsx`).
 */
export function Avatar(
  { name, photo, size = "md", coroa }: {
    name: string; photo?: string; size?: "xs" | "sm" | "md" | "lg" | "hero";
    /** Campeão da semana que passou: a coroa fica até a próxima temporada fechar. */
    coroa?: boolean;
  },
) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let vivo = true;
    setUrl(null);
    void photoURL(photo).then((u) => { if (vivo) setUrl(u); });
    return () => { vivo = false; };
  }, [photo]);

  const initials = name.trim().split(/\s+/).filter(Boolean);
  const label = initials.length > 1
    ? (initials[0]![0]! + initials[initials.length - 1]![0]!).toUpperCase()
    : (initials[0]?.[0] ?? "?").toUpperCase();
  const avatar = (
    <div className={`avatar ${size}`} aria-hidden="true">
      {url ? <img src={url} alt="" loading="lazy" referrerPolicy="no-referrer" /> : label}
    </div>
  );
  if (!coroa) return avatar;
  return (
    <span className={`avatar-coroa ${size}`} title="Campeão da semana">
      {avatar}<span className="coroa" aria-label="Campeão da semana">👑</span>
    </span>
  );
}

export function Empty(
  { emoji, title, children }: { emoji: string; title: string; children?: ReactNode },
) {
  return (
    <div className="empty">
      <div className="empty-big" aria-hidden="true">{emoji}</div>
      <strong>{title}</strong>
      {children}
    </div>
  );
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return <p className="note note-warn" role="alert">{children}</p>;
}

/**
 * O selo de quem assina.
 *
 * Vale porque é visível para o grupo — cosmético que ninguém vê não
 * vende. E é só isso: não dá vantagem no placar, que é a linha que este
 * app não cruza.
 */
export function SeloAssinante() {
  return <span className="selo-assinante" title="Assinante">★</span>;
}
